import { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import Flatpickr from "react-flatpickr";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import Alert from "../Alert";
import { alertContext } from "../contexts/AlertProvider";
import Pagination from "../layout/Pagination";
import Status from "./actions/Status";
import Suspend from "./actions/Suspend";
import refreshDriverApp from "../socket/RefreshDriverApp";
import logoutDriver from "../socket/LogoutDriver";
import Online from "./actions/Online";
import PermissionGate from "../layout/PermissionGate";
import socket from "../socket/socket";

const AllDrivers = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [type, setType] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [driver, setDriver] = useState({});
  const [popup, setPopup] = useState("");
  const [error, setError] = useState({});
  const [sharing, setSharing] = useState(0);
  const [alertConfig, setAlertConfig] = useContext(alertContext);
  const dateRangePickerRef = useRef(null);
  const fetchCache = useRef(new Set());
  const token = localStorage.getItem("token");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchDrivers = () => {
    axios
      .get(
        `${
          process.env.REACT_APP_API_ENDPOINT
        }all-drivers?page=${page}&limit=${limit}&search=${search}&type=${type.join(
          ","
        )}&start=${start}&end=${end}&delivaryCompanyId=${null}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        if (res.data.code === 1) {
          if (page > 1) {
            if (res.data.data.length === 0) {
              setPage(page - 1);
            } else {
              setData(res.data.data);
              setCount(res.data.count);
              setIsLoading(false);
            }
          } else {
            setData(res.data.data);
            setCount(res.data.count);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      })
      .catch(console.error);
  };
  const fetchSingleDriver = async (driverId) => {
    if (fetchCache.current.has(driverId)) return;
    fetchCache.current.add(driverId);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}driver-data/${driverId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.data) {
        const fullDriver = res.data.data;

        setData((prev) => {
          const exists = prev.some((d) => d._id === driverId);

          if (exists) {
            return prev.map((d) => (d._id === driverId ? fullDriver : d));
          }
        });
      }
    } catch (err) {
      console.error("Single Driver Fetch Failed:", err);
    } finally {
      setTimeout(() => fetchCache.current.delete(driverId), 3000);
    }
  };
  const processDriverType = (driverType) => {
    if (type.includes(driverType)) {
      const prevType = [...type];
      const updatedType = [];
      prevType.map((item) => {
        if (item !== driverType) {
          updatedType.push(item);
        }
      });
      setType(updatedType);
    } else {
      const prevType = [...type];
      prevType.push(driverType);
      setType(prevType);
    }
  };
  const reset = () => {
    setPage(1);
    setSearch("");
    setType([]);
    setStart("");
    setEnd("");
  };
  const openCalendar = () => {
    if (dateRangePickerRef.current) {
      dateRangePickerRef.current.flatpickr.open();
    }
  };
  const openPopup = (action, data) => {
    setDriver(data);
    setPopup(action);
  };
  const changeStatus = () => {
    if (driver.id !== undefined && driver.status !== undefined) {
      const status = driver.status === true ? false : true;
      axios
        .post(
          `${process.env.REACT_APP_API_ENDPOINT}change-driver-status`,
          {
            id: driver.id,
            status: status,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            fetchDrivers();
            logoutDriver(driver?.id);
          }
        })
        .catch(console.error);
    }
  };
  const changeAvailability = () => {
    if (driver.id !== undefined && driver?.busy !== undefined) {
      axios
        .patch(
          `${process.env.REACT_APP_API_ENDPOINT}change-driver-availability/${driver?.id}`,
          driver?.busy === true
            ? {
                busy: false,
              }
            : {
                busy: true,
              },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            fetchDrivers();
            refreshDriverApp(driver?.id);
          }
        })
        .catch(console.error);
    }
  };
  const resetPassword = () => {
    axios
      .post(
        process.env.REACT_APP_API_ENDPOINT + "reset-password-driver",
        driver,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            setError({});
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchDrivers();
          }
        }
      })
      .catch(console.error);
  };
  const deleteDriver = () => {
    if (driver.id !== undefined) {
      axios
        .post(
          `${process.env.REACT_APP_API_ENDPOINT}delete-driver`,
          {
            id: driver.id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            fetchDrivers();
            logoutDriver(driver?.id);
          }
        })
        .catch(console.error);
    }
  };
  const fetchDriverSharing = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}settings/get?key=driver-sharing`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.code === 1) {
        const value = Number(res?.data?.data?.value ?? 0);
        setSharing(value);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [page, search, type, start, end]);
  useEffect(() => {
    fetchDriverSharing();
  }, []);
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      if (!data) return;
      if (data?.driverId) fetchSingleDriver(data.driverId);
    };

    socket.on("admin:ordersUpdate", handleOrderUpdate);
    return () => socket.off("admin:ordersUpdate", handleOrderUpdate);
  }, []);

  return isLoading === true ? (
    <Loader />
  ) : (
    <div className="d-flex flex-column flex-root">
      <Alert />
      {/*begin::Page*/}
      <div className="page d-flex flex-row flex-column-fluid">
        {/*begin::Wrapper*/}
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
              {/*begin::Container*/}
              <div
                id="kt_toolbar_container"
                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
              >
                {/*begin::Page title*/}
                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                  {/*begin::Title*/}
                  <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                    Driver List
                  </h1>
                  {/*end::Title*/}
                </div>
                {/*end::Page title*/}
                {/*begin::Actions*/}
                <PermissionGate required={`view_driver_list`} fallback={<></>}>
                  <div className="d-flex align-items-center overflow-auto">
                    {/*begin::Button*/}
                    <button
                      onClick={() => {
                        processDriverType("Ahmadi Core Support Driver");
                      }}
                      className={
                        type.includes("Ahmadi Core Support Driver")
                          ? "btn btn-color-light bg-primary me-3"
                          : "btn btn-color-primary bg-body me-3"
                      }
                    >
                      Core
                    </button>
                    <br />
                    {/*end::Button*/}

                    {/*begin::Button*/}
                    <button
                      onClick={() => {
                        processDriverType("Freelance");
                      }}
                      className={
                        type.includes("Freelance")
                          ? "btn btn-color-light bg-success me-3"
                          : "btn btn-color-success bg-body me-3"
                      }
                    >
                      Freelance
                    </button>
                    <br />
                    {/*end::Button*/}

                    {/*begin::Button*/}
                    <button
                      onClick={() => {
                        processDriverType("Delivery Company Fleet Member");
                      }}
                      className={
                        type.includes("Delivery Company Fleet Member")
                          ? "btn btn-color-light bg-warning me-3 flex-shrink-0"
                          : "btn btn-color-warning bg-body me-3 flex-shrink-0"
                      }
                    >
                      Fleet Member
                    </button>

                    <br />
                    {/*end::Button*/}

                    {/*begin::Daterange*/}
                    <div className="d-flex position-relative flex-shrink-0">
                      <Flatpickr
                        ref={dateRangePickerRef}
                        value={[new Date(start), new Date(end)]}
                        options={{ dateFormat: "d-m-Y", mode: "range" }}
                        onChange={([start, end]) => {
                          if (start !== undefined && end !== undefined) {
                            setStart(moment(start).format("YYYY-MM-DD"));
                            setEnd(moment(end).format("YYYY-MM-DD"));
                          }
                        }}
                        className="form-control form-control-lg"
                        placeholder="Pick date"
                      />
                      <i
                        onClick={openCalendar}
                        className="bi-duotone bi-calendar fs-5 position-absolute"
                        style={{ top: "28%", right: "5%", cursor: "pointer" }}
                      ></i>
                    </div>
                    {/*end::Daterange*/}

                    {/*begin::Button*/}
                    <button
                      onClick={reset}
                      className="btn btn-color-danger bg-body mx-3"
                    >
                      Reset
                    </button>
                    {/*end::Button*/}
                  </div>
                </PermissionGate>
                {/*end::Actions*/}
              </div>
              {/*end::Container*/}
            </div>
            {/*end::Toolbar*/}

            {/*begin::Container*/}
            <div
              id="kt_content_container"
              className="d-flex flex-column-fluid align-items-start container-xxl"
            >
              {/*begin::Post*/}
              <div className="content flex-row-fluid" id="kt_content">
                {/*begin::Card*/}
                <PermissionGate required={`view_driver_list`}>
                  <div className="card">
                    {/*begin::Card header*/}
                    <div className="card-header border-0 pt-6">
                      {/*begin::Search*/}
                      <div className="my-1 w-100">
                        <input
                          type="text"
                          value={search}
                          autoComplete="off"
                          onChange={(e) => {
                            setSearch(e.target.value);
                          }}
                          id="searchDriverInput"
                          className="form-control w-100"
                          placeholder="Search Driver Name, Phone"
                        />
                      </div>
                      {/*end::Search*/}
                    </div>
                    {/*end::Card header*/}

                    {/*begin::Card body*/}
                    <div
                      className="card-body py-4 text-center"
                      id="filterDataWrap"
                    >
                      {/*begin::Table*/}
                      {data?.length === 0 ? (
                        <div className="text-center w-100">
                          <img
                            className="w-25 mx-auto py-20"
                            src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                            alt="no-content"
                          />
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table align-middle table-row-dashed fs-6 gy-5">
                            <thead>
                              <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                <th className="text-center min-w-100px">
                                  Profile Image
                                </th>
                                <th className="text-center min-w-100px">
                                  Driver Name
                                </th>
                                <th className="text-center min-w-100px">
                                  Sharing
                                </th>
                                <th className="text-center min-w-100px">
                                  Location Updated
                                </th>
                                <th className="text-center min-w-100px">
                                  Phone Number
                                </th>
                                <th className="text-center min-w-100px">
                                  Status
                                </th>
                                <th className="text-center min-w-100px">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-gray-600 fw-semibold">
                              {data &&
                                data?.map((item, index) => {
                                  return (
                                    <tr className="text-center" key={index}>
                                      <td>
                                        {/*begin:: Avatar */}
                                        <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                          <Link
                                            to={`/drivers/driver-details/${item?._id}`}
                                          >
                                            <div className="symbol-label">
                                              <img
                                                src={
                                                  process.env
                                                    .REACT_APP_API_PUBLIC_URL +
                                                  item?.profilePhoto
                                                }
                                                alt={item?.name}
                                                className="w-100"
                                              />
                                            </div>
                                          </Link>
                                        </div>
                                        {/*end::Avatar*/}
                                      </td>
                                      <td>
                                        <Link
                                          to={`/drivers/driver-details/${item?._id}`}
                                          className="text-gray-800 text-hover-primary"
                                        >
                                          {item?.name}
                                        </Link>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            item?.sharing > 0 &&
                                            "badge bg-primary text-white"
                                          }
                                        >
                                          {item?.sharing > 0
                                            ? `${item?.sharing}%`
                                            : sharing > 0
                                            ? `${sharing}%`
                                            : 0}
                                        </span>
                                      </td>
                                      <td>{item?.lastOnline?.text ?? "---"}</td>
                                      <td>{item?.workPhone}</td>
                                      <td>
                                        <Status
                                          busy={item?.busy}
                                          online={item?.online}
                                          status={item?.status}
                                          orders={item?.orders}
                                        />
                                      </td>
                                      <td className="">
                                        <div className="btn-group">
                                          {/*begin::View*/}
                                          <PermissionGate
                                            required={`view_driver_details`}
                                            fallback={<></>}
                                          >
                                            <Link
                                              to={`/drivers/driver-details/${item?._id}`}
                                              className="btn px-2 btn-outline btn-success btn-color-white btn-sm"
                                              title="Show Details"
                                            >
                                              <i className="bi-duotone bi-eye fs-5"></i>
                                            </Link>
                                          </PermissionGate>
                                          {/*end::View*/}
                                          {/*begin::Edit*/}
                                          <PermissionGate
                                            required={`edit_driver_details`}
                                            fallback={<></>}
                                          >
                                            <Link
                                              to={`/drivers/edit-driver/${item?._id}`}
                                              className="btn px-2 btn-outline btn-primary btn-color-white btn-sm"
                                              title="Edit"
                                            >
                                              <i className="bi-duotone bi-pen fs-5"></i>
                                            </Link>
                                          </PermissionGate>
                                          {/*end::Edit*/}
                                          {/*begin::Suspend*/}
                                          <Suspend
                                            openPopup={openPopup}
                                            item={item}
                                          />
                                          {/*end::Suspend*/}
                                          {/*begin::Online*/}
                                          <Online
                                            id={item?._id}
                                            openPopup={openPopup}
                                            busy={item?.busy}
                                          />
                                          {/*end::Online*/}
                                          {/*begin::Change Password*/}
                                          <PermissionGate
                                            required={`reset_driver_password`}
                                            fallback={<></>}
                                          >
                                            <span
                                              onClick={() => {
                                                openPopup(
                                                  "reset-password",
                                                  item
                                                );
                                              }}
                                              className="btn px-2 btn-outline btn-info btn-color-white btn-sm"
                                              title="Reset Password"
                                            >
                                              <i className="bi-duotone bi-key fs-5"></i>
                                            </span>
                                          </PermissionGate>
                                          {/*end::Change Password*/}

                                          {/*begin::Delete*/}
                                          <PermissionGate
                                            required={`delete_driver`}
                                            fallback={<></>}
                                          >
                                            <span
                                              onClick={() => {
                                                openPopup("delete", {
                                                  id: item._id,
                                                });
                                              }}
                                              className="btn px-3 btn-outline btn-danger btn-color-white btn-sm"
                                              title="Delete Driver"
                                            >
                                              <i className="bi-duotone bi-trash fs-5"></i>
                                            </span>
                                          </PermissionGate>

                                          {/*end::Delete*/}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/*end::Table*/}
                      {/*begin::Paginate*/}
                      {count && Math.ceil(count / limit) > 1 ? (
                        <Pagination
                          page={page}
                          setPage={setPage}
                          count={count}
                          limit={limit}
                        />
                      ) : (
                        ""
                      )}
                      {/*begin::Paginate*/}
                    </div>
                    {/*end::Card body*/}
                  </div>
                </PermissionGate>
                {/*end::Card*/}
              </div>
              {/*end::Post*/}
            </div>
          </div>
          {/* start :: Change Status */}
          <div
            className={
              popup === "change-status"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
            id="kt_modal_1"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    CHANGE STATUS
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={changeStatus}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Change Status */}
          {/* start :: Change Availability */}
          <div
            className={
              popup === "change-availability"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    CHANGE AVAILABILITY
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={changeAvailability}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Change Availability */}
          {/* start :: Reset Password */}
          <div
            className={
              popup === "reset-password"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
            id="kt_modal_1"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-info text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    RESET PASSWORD
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={resetPassword}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Reset Password */}
          {/* start :: delete */}
          <div
            className={
              popup === "delete" ? "modal fade show d-block" : "modal fade"
            }
            tabIndex={-1}
            id="kt_modal_1"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-danger text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">DELETE</h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={deleteDriver}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: delete */}
          <Footer />
        </div>
        {/*end::Wrapper*/}
      </div>
      {/*end::Page*/}
    </div>
  );
};

export default AllDrivers;
